/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import {
	ActionIcon,
	Box,
	Container,
	Group,
} from '@mantine/core';
import React, { memo } from 'react';

import {
	IconArrowBarLeft,
	IconArrowBarRight,
	IconMenu2,
} from '@tabler/icons-react';
import { getRoleLabel } from '@utils/functions';
import { Title } from '../../ui/Title/Title';

import { ROLES } from '@config/constants/roles';
import { useAuthorization } from '@lib/authorization';
import { useTranslation } from 'react-i18next';
import { Select } from '../Select/Select';
import classes from './TopBar.module.css';
import { UserProfileMenu } from './components/UserProfileMenu';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type TTopBarProps = {
	title: string;
	isMobile: boolean;
	isBigScreen: boolean;
	onToggleSidebar: () => void;
	userName?: string;
	userRole?: string;
	avatarSrc?: string;
	onLogout?: () => void;
	onExpand: () => void;
	isExpanded: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const TopBar: React.FC<TTopBarProps> = memo(
	({
		title,
		isMobile,
		isBigScreen,
		onToggleSidebar,
		userName = 'User',
		userRole = 'USER',
		onLogout,
		onExpand,
		isExpanded,
		avatarSrc,
	}) => {
		const { t } = useTranslation();

		const { checkRole } = useAuthorization();
		/* ******************************** RENDERING ******************************* */
		return (
			<Container
				size="100%"
				className={classes.container}
			>
				<Group w="100%" justify="space-between">
					<Box className={classes.wrapperLeft}>
						<ActionIcon
							visibleFrom="sm"
							color="var(--mantine-color-igluBorder-9)"
							radius="25px"
							mr="10px"
							onClick={() => {
								onExpand();
							}}
							variant="outline"
							data-testid="expand-button"
						>
							{isExpanded ? (
								<IconArrowBarLeft
									color="var(--mantine-color-igluPrimary-9)"
									size="16px"
								/>
							) : (
								<IconArrowBarRight
									color="var(--mantine-color-igluPrimary-9)"
									size="16px"
								/>
							)}
						</ActionIcon>
						{!isMobile ? (
							<Box className={classes.containerTitle}>
								<Title
									c="var(--mantine-color-igluPrimary-9)"
									order={4}
									textWrap="wrap"
									data-testid="topbar-title"
								>
									{title}
								</Title>
							</Box>
						) : (
							<ActionIcon
								variant="filled"
								size={40}
								radius="12px"
								color="var(--mantine-color-igluPrimaryHover-9)"
								data-testid="mobile-menu-button"
								onClick={onToggleSidebar}
							>
								<IconMenu2
									color="var(--mantine-color-igluInnerForm-9)"
									size={24}
									stroke={1.5}
									data-testid="icon-menu2"
								/>
							</ActionIcon>
						)}
					</Box>
					<Box className={classes.wrapperRight}>
						{checkRole([ROLES.ADMIN, ROLES.SUPERADMIN]) &&
							isBigScreen && (
								<Select
									data={[
										t('global.organization'),
										t('global.offset'),
									]}
									placeholder={t('global.add')}
									clearable
									size="md"
									disabled
								/>
							)}

						<UserProfileMenu
							userName={userName}
							userRole={t(
								`roles.${getRoleLabel(userRole)}`
							)}
							onLogout={onLogout}
							avatarSrc={avatarSrc}
						/>
					</Box>
				</Group>
			</Container>
		);
	}
);
